import { reconcileElements } from "@excalidraw/excalidraw";
import type {
  ExcalidrawElement,
  FileId,
} from "@excalidraw/excalidraw/element/types";
import { getSceneVersion } from "@excalidraw/excalidraw/element";
import type Portal from "../collab/Portal";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";
import {
  compressData,
  decompressData,
} from "@excalidraw/excalidraw/data/encode";
import { MIME_TYPES } from "@excalidraw/excalidraw/constants";
import type { SyncableExcalidrawElement } from ".";
import { getSyncableElements } from ".";
import type { Socket } from "socket.io-client";
import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import { initializeApp } from "firebase/app";
import { getStorage } from "firebase/storage";

const BACKEND_V2 = import.meta.env.VITE_APP_BACKEND_V2_GET_URL.replace(
  /\/?$/,
  "/",
);

const backendUrl = (...segments: string[]) =>
  `${BACKEND_V2}${segments.map(encodeURIComponent).join("/")}`;

const prefixSegments = (prefix: string) =>
  prefix.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);

// Kept for the Excalidraw+ export path. Normal scene, room, and file storage
// below uses the self-hosted backend.
let FIREBASE_CONFIG: Record<string, any>;
try {
  FIREBASE_CONFIG = JSON.parse(import.meta.env.VITE_APP_FIREBASE_CONFIG);
} catch (error: any) {
  console.warn(
    `Error JSON parsing firebase config. Supplied value: ${
      import.meta.env.VITE_APP_FIREBASE_CONFIG
    }`,
  );
  FIREBASE_CONFIG = {};
}

let firebaseApp: ReturnType<typeof initializeApp> | null = null;
let firebaseStorage: ReturnType<typeof getStorage> | null = null;

const initializeFirebase = () => {
  if (!firebaseApp) {
    firebaseApp = initializeApp(FIREBASE_CONFIG);
  }
  return firebaseApp;
};

export const loadFirebaseStorage = async () => {
  if (!firebaseStorage) {
    firebaseStorage = getStorage(initializeFirebase());
  }
  return firebaseStorage;
};

const uploadBuffer = async (url: string, buffer: Uint8Array) => {
  const response = await fetch(url, {
    method: "PUT",
    body: buffer,
  });

  if (!response.ok) {
    throw new Error(`Backend upload failed: ${response.status}`);
  }
};

// rooms
// -----------------------------------------------------------------------------

const encodeElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
) => {
  const sceneVersion = getSceneVersion(elements);
  const json = JSON.stringify(elements);
  const encoded = new TextEncoder().encode(json);

  return compressData<{ sceneVersion: number }>(encoded, {
    encryptionKey: key,
    metadata: { sceneVersion },
  });
};

const decodeElements = async (
  buffer: ArrayBuffer,
  roomKey: string,
): Promise<readonly SyncableExcalidrawElement[]> => {
  const { data } = await decompressData<{ sceneVersion: number }>(
    new Uint8Array(buffer),
    {
      decryptionKey: roomKey,
    },
  );

  const decodedData = new TextDecoder("utf-8").decode(data);
  const elements = JSON.parse(decodedData);

  return getSyncableElements(restoreElements(elements, null));
};

const roomUrl = (roomId: string) => backendUrl("rooms", roomId);

class BackendSceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => {
    return BackendSceneVersionCache.cache.get(socket);
  };
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    BackendSceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);

    return BackendSceneVersionCache.get(portal.socket) === sceneVersion;
  }
  // if no room exists, consider the room saved so that we don't unnecessarily
  // prevent unload (there's nothing we could do at that point anyway)
  return true;
};

const loadRoomFromBackend = async (
  roomId: string,
  roomKey: string,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  const response = await fetch(roomUrl(roomId));

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Backend room load failed: ${response.status}`);
  }

  return decodeElements(await response.arrayBuffer(), roomKey);
};

export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (
    // bail if no room exists as there's nothing we can do at this point
    !roomId ||
    !roomKey ||
    !socket ||
    isSavedToFirebase(portal, elements)
  ) {
    return null;
  }

  const prevStoredElements = await loadRoomFromBackend(roomId, roomKey);
  const storedElements = prevStoredElements
    ? getSyncableElements(
        reconcileElements(
          elements,
          prevStoredElements as unknown as readonly RemoteExcalidrawElement[],
          appState,
        ),
      )
    : elements;

  await uploadBuffer(
    roomUrl(roomId),
    await encodeElements(roomKey, storedElements),
  );

  BackendSceneVersionCache.set(socket, storedElements);

  return storedElements;
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  const elements = await loadRoomFromBackend(roomId, roomKey);

  if (socket && elements) {
    BackendSceneVersionCache.set(socket, elements);
  }

  return elements;
};

// files
// -----------------------------------------------------------------------------

const fileUrl = (prefix: string, id: FileId) =>
  backendUrl("files", ...prefixSegments(prefix), id);

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        await uploadBuffer(fileUrl(prefix, id), buffer);
        savedFiles.push(id);
      } catch (error: any) {
        console.error(error);
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const response = await fetch(fileUrl(prefix, id));

        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};
